import { useEffect, useMemo, useState } from "react";
import { History, RefreshCw, Loader2, GitCompare, RotateCcw, Sparkles, User, Tag, Check, X, AlertTriangle, Search } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import RichContent from "../public/RichContent";

const LABEL_PRESETS = ["Initial Import", "SEO Review", "AI Generated", "Human Edited", "Legal Update", "Seasonal Update", "Published Version"];
const CONTENT_TYPES = ["intro", "main", "info", "safety", "additional", "faqs", "keywords", "internal links", "related content", "external references", "technical SEO"];

const sourceBadge = (src) => {
  const m = {
    ai: { label: "AI", cls: "border-gold/40 text-gold", icon: Sparkles },
    manual: { label: "Manual", cls: "border-blue-400/40 text-blue-300", icon: User },
    restore: { label: "Restored", cls: "border-sky-400/40 text-sky-300", icon: RotateCcw },
  }[src] || { label: src || "—", cls: "border-white/20 text-white/60", icon: User };
  const Icon = m.icon;
  return <Badge variant="outline" className={`text-[10px] gap-0.5 ${m.cls}`}><Icon className="h-2.5 w-2.5" />{m.label}</Badge>;
};
const fmt = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export default function VersionHistory({ entityType, entityId, open, onOpenChange, onRestored }) {
  const [versions, setVersions] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState([]);
  const [diff, setDiff] = useState(null);       // {a,b,diff}
  const [restoreTarget, setRestoreTarget] = useState(null); // version obj
  const [restoreDiff, setRestoreDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  const [labelEdit, setLabelEdit] = useState(null);

  const load = () => {
    if (!entityId) return;
    setLoading(true);
    const params = { entity_type: entityType, entity_id: entityId, limit: 20, ...filters };
    api.get("/admin/seo/versions", { params }).then(({ data }) => {
      setVersions(data.versions || []); setTotal(data.total || 0);
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) { setSelected([]); load(); } /* eslint-disable-next-line */ }, [open, entityId, JSON.stringify(filters)]);

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 2 ? [s[1], id] : [...s, id]));

  const openCompare = async () => {
    if (selected.length !== 2) { toast.error("Select exactly two versions to compare."); return; }
    const byNo = [...(versions || [])].filter((v) => selected.includes(v.version_id)).sort((a, b) => a.version_no - b.version_no);
    try {
      const { data } = await api.get(`/admin/seo/versions/compare/${byNo[0].version_id}/${byNo[1].version_id}`);
      setDiff(data); setShowAllFields(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const openRestore = async (v) => {
    const newest = (versions || [])[0];
    try {
      const { data } = await api.get(`/admin/seo/versions/compare/${v.version_id}/${newest.version_id}`);
      setRestoreDiff(data); setRestoreTarget(v);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const doRestore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/seo/versions/${restoreTarget.version_id}/restore`);
      if (!data.ok) { toast.error(data.error || "Restore failed."); return; }
      toast.success(`Restored from v${data.restored_from} — saved as new v${data.new_version}.`);
      setRestoreTarget(null); setRestoreDiff(null); load(); onRestored && onRestored();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveLabel = async (v, label) => {
    try { await api.put(`/admin/seo/versions/${v.version_id}/label`, { label }); setLabelEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const setF = (patch) => setFilters((f) => { const n = { ...f, ...patch }; Object.keys(n).forEach((k) => !n[k] && delete n[k]); return n; });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="version-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-gold" /> Version History</DialogTitle>
          <DialogDescription>Last 20 saves of this item's SEO & content. Compare any two, or restore a previous version (restoring always creates a new entry — nothing is deleted).</DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="grid sm:grid-cols-3 gap-2 rounded-lg border border-white/10 bg-white/5 p-3" data-testid="version-filters">
          <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" /><Input placeholder="Search notes/summary…" className="pl-8 bg-black/30 border-white/15 h-9 text-sm" onChange={(e) => setF({ q: e.target.value })} data-testid="version-search" /></div>
          <select className="cf-select bg-black/30 border border-white/15 rounded-md px-2 h-9 text-sm" onChange={(e) => setF({ source: e.target.value })} data-testid="version-filter-source">
            <option value="">All sources</option><option value="manual">Manual</option><option value="ai">AI</option><option value="restore">Restored</option>
          </select>
          <select className="cf-select bg-black/30 border border-white/15 rounded-md px-2 h-9 text-sm" onChange={(e) => setF({ status: e.target.value })} data-testid="version-filter-status">
            <option value="">All statuses</option><option value="published">Published</option><option value="draft">Draft</option>
          </select>
          <Input placeholder="Administrator…" className="bg-black/30 border-white/15 h-9 text-sm" onChange={(e) => setF({ actor: e.target.value })} data-testid="version-filter-actor" />
          <select className="cf-select bg-black/30 border border-white/15 rounded-md px-2 h-9 text-sm" onChange={(e) => setF({ content_type: e.target.value })} data-testid="version-filter-type">
            <option value="">All content types</option>{CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex gap-1">
            <Input type="date" className="bg-black/30 border-white/15 h-9 text-xs" onChange={(e) => setF({ date_from: e.target.value })} data-testid="version-date-from" />
            <Input type="date" className="bg-black/30 border-white/15 h-9 text-xs" onChange={(e) => setF({ date_to: e.target.value })} data-testid="version-date-to" />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-white/50">{total} version(s){selected.length === 2 ? " · 2 selected" : selected.length === 1 ? " · 1 selected" : ""}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1" disabled={selected.length !== 2} onClick={openCompare} data-testid="version-compare-btn"><GitCompare className="h-3.5 w-3.5" /> Compare</Button>
            <Button size="sm" variant="ghost" onClick={load} data-testid="version-refresh"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* List */}
        <div className="space-y-2">
          {loading ? <div className="p-6 text-center text-white/40 text-sm flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> :
            !versions || versions.length === 0 ? <p className="text-white/40 text-sm py-6 text-center" data-testid="version-empty">No versions{entityId ? " match these filters." : " — save this item first."}</p> :
              versions.map((v) => (
                <div key={v.version_id} className="rounded-lg border border-white/10 bg-white/5 p-3" data-testid={`version-row-${v.version_no}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Checkbox checked={selected.includes(v.version_id)} onCheckedChange={() => toggleSel(v.version_id)} data-testid={`version-check-${v.version_no}`} />
                    <span className="font-semibold text-white">v{v.version_no}</span>
                    {sourceBadge(v.source)}
                    {v.status && <Badge variant="outline" className={`text-[10px] ${v.status === "published" ? "border-green-500/40 text-green-300" : "border-white/20 text-white/50"}`}>{v.status}</Badge>}
                    {v.model && <span className="text-[10px] text-white/40">{v.model}</span>}
                    <span className="text-[11px] text-white/45 ml-auto">{fmt(v.created_at)} · {v.actor}</span>
                    <Button size="sm" variant="outline" className="h-7 gap-1 border-sky-400/30 text-sky-300" onClick={() => openRestore(v)} data-testid={`version-restore-${v.version_no}`}><RotateCcw className="h-3.5 w-3.5" /> Restore</Button>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap pl-6">
                    <span className="text-xs text-white/60">{v.summary}</span>
                    {(v.content_types || []).map((c, i) => <span key={i} className="text-[10px] rounded-full bg-white/10 text-white/50 px-2 py-0.5">{c}</span>)}
                  </div>
                  <div className="mt-1.5 pl-6">
                    {labelEdit === v.version_id ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <Input autoFocus defaultValue={v.label || ""} className="h-7 w-48 bg-black/30 border-white/15 text-xs" onKeyDown={(e) => e.key === "Enter" && saveLabel(v, e.target.value)} data-testid={`version-label-input-${v.version_no}`} list="label-presets" />
                        <datalist id="label-presets">{LABEL_PRESETS.map((l) => <option key={l} value={l} />)}</datalist>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-green-400" onClick={(e) => saveLabel(v, e.currentTarget.parentElement.querySelector("input").value)}><Check className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-white/40" onClick={() => setLabelEdit(null)}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <button onClick={() => setLabelEdit(v.version_id)} className="text-[11px] text-white/45 hover:text-gold flex items-center gap-1" data-testid={`version-label-${v.version_no}`}>
                        <Tag className="h-3 w-3" /> {v.label || "Add note"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
        </div>

        {/* Compare dialog */}
        <Dialog open={!!diff} onOpenChange={(o) => !o && setDiff(null)}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="version-compare-dialog">
            <DialogHeader>
              <DialogTitle>Compare v{diff?.a.version_no} → v{diff?.b.version_no}</DialogTitle>
              <DialogDescription>{diff?.diff.changed_count} field(s) changed. Older on the left, newer on the right.</DialogDescription>
            </DialogHeader>
            <label className="flex items-center gap-2 text-xs text-white/60"><Checkbox checked={showAllFields} onCheckedChange={setShowAllFields} /> Show unchanged fields too</label>
            <DiffView diff={diff?.diff} showAll={showAllFields} />
          </DialogContent>
        </Dialog>

        {/* Restore confirm dialog */}
        <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && (setRestoreTarget(null), setRestoreDiff(null))}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="version-restore-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-sky-300" /> Restore v{restoreTarget?.version_no}?</DialogTitle>
              <DialogDescription>The current version will be preserved as a new history entry — nothing is deleted. Below: the version you're restoring (left) vs the current version (right).</DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> This will overwrite the live SEO/content with v{restoreTarget?.version_no}'s data.</div>
            <DiffView diff={restoreDiff?.diff} showAll={false} labels={["Restoring (v" + (restoreTarget?.version_no) + ")", "Current"]} />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRestoreTarget(null); setRestoreDiff(null); }}>Cancel</Button>
              <Button className="cf-gold-btn gap-1" onClick={doRestore} disabled={busy} data-testid="version-restore-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Restore & Save as New Version</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function DiffView({ diff, showAll, labels = ["Older", "Newer"] }) {
  const fields = useMemo(() => (diff?.fields || []).filter((f) => showAll || f.changed), [diff, showAll]);
  const statusCls = { added: "text-green-400", removed: "text-red-400", modified: "text-amber-400", same: "text-white/40" };
  if (!diff) return null;
  return (
    <div className="space-y-3" data-testid="diff-view">
      <div className="grid grid-cols-2 gap-3 text-[11px] uppercase tracking-wider text-white/40 sticky top-0">
        <span>{labels[0]}</span><span>{labels[1]}</span>
      </div>
      {fields.length === 0 ? <p className="text-white/40 text-sm text-center py-4">No changes.</p> : fields.map((f) => (
        <div key={f.key} data-testid={`diff-field-${f.key}`}>
          <p className="text-xs font-medium text-white/70 mb-1 flex items-center gap-2">{f.label} <span className={`text-[10px] ${statusCls[f.status]}`}>{f.changed ? f.status : ""}</span></p>
          <div className="grid grid-cols-2 gap-3">
            <DiffCell value={f.old} type={f.type} tone={f.changed ? "old" : ""} />
            <DiffCell value={f.new} type={f.type} tone={f.changed ? "new" : ""} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DiffCell({ value, type, tone }) {
  const bg = tone === "old" ? "bg-red-500/5 border-red-500/20" : tone === "new" ? "bg-green-500/5 border-green-500/20" : "border-white/10 bg-black/20";
  return (
    <div className={`rounded-md border p-2 text-xs min-h-[2rem] max-h-52 overflow-auto ${bg}`}>
      {!value ? <span className="text-white/25 italic">empty</span> :
        type === "html" ? <RichContent html={value} className="text-xs" /> :
          type === "json" ? <pre className="whitespace-pre-wrap text-[11px] text-white/70">{value}</pre> :
            <span className="text-white/80 break-words">{value}</span>}
    </div>
  );
}
